import { Accordion, Button, ButtonsGroup, Card, Empty, GridCell, Heading, Icon, InputSelect } from "#/atoms";
import { DESKTOP_SPACING, HPOSITION, NATURAL } from "@/theme";
import { FC, memo, useCallback, useState } from "react";
import { PhotoFile, PhotoProps } from "./OrderService.interface";
import { StyledAcceptButton, StyledAnnotationLabel,  StyledCounter, StyledDropzone, StyledEditButton, StyledExpandButton, StyledFullscreen, StyledImage, StyledImageSection, StyledPhotoImg, StyledPhotos, StyledThumb } from "./OrderService.styled";

import AnnotationIcon from "@/assets/icons/Annotation";
import { EditPhotoModal } from "#/organisms";
import ExpandIcon from '@/assets/icons/Expand';
import { HeadingIcon } from "#/molecules";
import { OrderFile } from "@/types";
import { useDropzone } from 'react-dropzone';
import { useModal } from "@/hooks";

const OfferedService: FC<PhotoProps> = ({
  draftFiles,
  isOffered = false,
  name,
  onDeleteFiles,
  onDownloadFiles,
  onUploadFiles,
  serviceId,
  uploadedFiles,
}) => {
  const { showModal: showEditPhotoModal, onCloseModal: onEditPhotoClose, onOpenModal: onEditPhotoOpen } = useModal();

  const [files, setFiles] = useState<PhotoFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [fullscreen, setFullscreen] = useState<OrderFile | null>(null);
  const [imageToEdit, setImageToEdit] = useState<string | null>(null);
  const accordionState = 'open';

  const handleSelectFile = useCallback((checked: boolean, value: string) => {
    setSelectedFiles((state) => {
      if (checked) {
        return [...state, value];
      }

      return state.filter((item) => item !== value);
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!draftFiles) {
      return;
    }
    setSelectedFiles(draftFiles.map((item) => item.uploadFileId))
  }, [draftFiles]);

  const handleEditPhoto = useCallback((id: string) => {
    setImageToEdit(id);
    onEditPhotoOpen();
  }, [draftFiles]);

  // const handleCombinePhoto = useCallback(() => {
  // }, [draftFiles]);

  const handleDeleteFiles = useCallback(() => {
    // if (!draftFiles) {
    //   return;
    // }
    // const filesToDelete = selectedFiles.reduce((acc: string[], cur) => {
    //   const item : OrderFile | undefined = uploadedFiles.find((v) => v.uploadFileId === cur);

    //   if (item) {
    //     return [...acc, item.uploadFileId]
    //   }

    //   return acc;
    // }, []);
    
    onDeleteFiles?.(serviceId, selectedFiles, isOffered, false)
  }, [selectedFiles, draftFiles]);

  const handleDownloadFiles = useCallback(() => {
    onDownloadFiles(serviceId, uploadedFiles.map((item) => item.uploadFileId), false);
  }, [serviceId]);

  const handleFullscreenOpen = useCallback((id: string) => {
    const file = uploadedFiles.find((item) => item.uploadFileId === id) || (draftFiles && draftFiles.find((item) => item.uploadFileId === id));

    if (file) {
      setFullscreen(file);
    }
  }, [uploadedFiles, draftFiles]);

  const handleFullscreenClose = useCallback(() => {
    setFullscreen(null);
  }, []);

  const handleSavePhoto = useCallback(() => {
    onEditPhotoClose();
  }, []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setFiles(acceptedFiles.map((file) =>
      Object.assign(file, {
          id: file.name,
          preview: URL.createObjectURL(file),
      })
    ));
    await onUploadFiles?.(serviceId, acceptedFiles, false, true);
    setFiles([]);
  }, [serviceId]);

  const {
    getRootProps,
    getInputProps,
    open
  } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.gif'],
    },
    noClick: true,
    onDrop
  });

  const getThumb = (id: string, img: string, hasAnnotation: boolean, index: number) => {
    const checked = selectedFiles.includes(id);

    return (
      <>
        <StyledEditButton></StyledEditButton>
        <StyledAcceptButton type="button">
          <InputSelect type="checkbox" checked={checked} value={id} onChangeValue={handleSelectFile} labelPosition={HPOSITION.CENTER} />
        </StyledAcceptButton>
        {hasAnnotation && <StyledAnnotationLabel><AnnotationIcon /></StyledAnnotationLabel>}
        <StyledCounter>{index + 1}</StyledCounter>
        <StyledExpandButton type="button" onClick={() => handleFullscreenOpen(id)}><ExpandIcon /></StyledExpandButton>
        <StyledPhotoImg img={img} />
      </>
    )
  }

  const getFilesThumb = (data: PhotoFile[]) => {
    return data.map(({ id, preview }, index) => {
      return (
        <StyledThumb key={id}>
          {getThumb(id, preview, false,  index)}
        </StyledThumb>
      )
    });
  }

  const getUploadedThumb = (data: OrderFile[]) => {
    return data.map(({ uploadFileId, thumbURL, hasAnnotation }, index) => {
      return (
        <StyledThumb key={uploadFileId}>
          {getThumb(uploadFileId, thumbURL, hasAnnotation, index)}
        </StyledThumb>
      )
    });
  }

  return (
    <Card>
        <Accordion
          header={<HeadingIcon icon="contacts" mb={`${DESKTOP_SPACING.XXL}px`} title={name} />}
          showArrow
          state={accordionState}
        >
          {/* <StyledOrderService>
            <Typo fontSize={FONT_SIZE.SM} margin="0">Photographer: <strong>{selectedWorkerName ? selectedWorkerName : '--'}</strong></Typo>
            <Button buttonSize="md" actionType="ghost" onClick={onAskToChangeWorker}>Change Photographer</Button>
          </StyledOrderService>
          <Flex alignItems="center" gap={`${DESKTOP_SPACING.MD}px`}>
            <Heading scale="h4" fontColor={PRIMARY_BLUE.DARK}>{name}</Heading>
            {amount && <Typo fontColor={NATURAL.G} weight={FONT_WEIGHT.SEMIBOLD}>X{amount}</Typo>}
          </Flex> */}

          {uploadedFiles && uploadedFiles.length > 0 && (
            <StyledImageSection>
              <Heading scale="h4" margin={`0 0 ${DESKTOP_SPACING.XXXL}px`}>Selected</Heading>
              <StyledPhotos>
                {getUploadedThumb(uploadedFiles)}
              </StyledPhotos>
              <ButtonsGroup fullWidth>
                <Button onClick={open}>Send Edited</Button>
              </ButtonsGroup>
            </StyledImageSection>
          )}
          
          <StyledDropzone {...getRootProps()}>
            <input type="file" {...getInputProps()} />
            {uploadedFiles && uploadedFiles.length > 0 && <Heading scale="h4" margin={`0 0 ${DESKTOP_SPACING.XXXL}px`}>Outtakes</Heading>}
            {((draftFiles && draftFiles.length === 0) || !draftFiles) && files.length === 0 ? (
              <Empty>No photos are added yet. You can start adding new photos.</Empty>
            ) : (
              <StyledPhotos>
                {getFilesThumb(files)}
                {draftFiles && getUploadedThumb(draftFiles)}
              </StyledPhotos>
            )}
          </StyledDropzone>
          <ButtonsGroup fullWidth>
            <GridCell>
              <Button
                actionType="outline"
                buttonColor="secondary"
                state={selectedFiles.length > 0 ? 'default' : 'disabled'}
                onClick={handleDeleteFiles}
              >
                Delete Selected
              </Button>
              {/* <Button
                state={selectedFiles.length >= 3 ? 'default' : 'disabled'}
                onClick={handleCombinePhoto}
              >
                Combine to HDR
              </Button> */}
            </GridCell>
            <Button actionType="outline" onClick={handleDownloadFiles}>Download All</Button>
            <Button actionType="outline" onClick={handleSelectAll}>Select All</Button>
            <Button onClick={open}>Upload New</Button>
          </ButtonsGroup>
          {fullscreen && (
            <StyledFullscreen onClick={handleFullscreenClose}>
              <Button actionType="ghost" onClick={handleFullscreenClose}><Icon type="cross" fillColor={NATURAL.A} /></Button>
              <StyledImage src={fullscreen.fullURL} />
            </StyledFullscreen>
          )}
          {showEditPhotoModal && imageToEdit && (
            <EditPhotoModal
              imageUrl={imageToEdit}
              loading={false}
              onClose={onEditPhotoClose}
              onSavePhoto={handleSavePhoto}
            />
          )}
      </Accordion>
     </Card>
  );
};

export default memo(OfferedService);